import React, { useEffect, useState } from 'react';
import { BUTTON, POPUP_TYPE, REPORTS_COLUMN, ROUTES, TITLES } from '../../../constants';
import { SearchBarReport, TitleBar, PaginatedTable } from '../../common';
import { useLoader } from '../../../context/LoaderContext';
import { popup } from '../../../utils';
import { vendorReport, exportWipReport } from '../../../service/report';
import { Button } from '../../shared';
import { reportDataCompiler } from '../../../utils/dataCompiler/wipReportCompiler';
import { useSearchParams } from 'react-router-dom';
import DateRangePicker from '../../common/DateRangePicker';

export const ReportsContent = () => {
  const { setLoading } = useLoader();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or default values
  const [searchTerm1, setSearchTerm1] = useState(searchParams.get('lot') || '');
  const [searchTerm2, setSearchTerm2] = useState(searchParams.get('sku') || '');
  const [searchTerm3, setSearchTerm3] = useState(searchParams.get('po') || '');
  const [search, setSearch] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [activeSearchParams, setActiveSearchParams] = useState({});
  const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);
  const [paginationData, setPaginationData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [startDate, setStartDate] = useState(searchParams.get('start_date') || '');
  const [endDate, setEndDate] = useState(searchParams.get('end_date') || '');
  const [datePreset, setDatePreset] = useState(searchParams.get('date_preset') || '');

  // Update URL params when values change
  const updateUrlParams = (params = {}) => {
    const newParams = {};

    // Only include non-empty values
    if (params.lot) newParams.lot = params.lot;
    if (params.sku) newParams.sku = params.sku;
    if (params.po) newParams.po = params.po;
    if (params.page) newParams.page = params.page.toString();
    if (params.start_date) newParams.start_date = params.start_date;
    if (params.end_date) newParams.end_date = params.end_date;
    if (params.date_preset) newParams.date_preset = params.date_preset;

    setSearchParams(newParams, { replace: true });
  };

  const getReport = (additionalParams = {}) => {
    let data = {};
    if (searchTerm1.length > 0) data.lot_name = searchTerm1;
    if (searchTerm2.length > 0) data.default_code = searchTerm2;
    if (searchTerm3.length > 0) data.po_name = searchTerm3;

    // Check if all search terms are empty
    if (Object.keys(data).length === 0) {
      setReportData([]);
      setActiveSearchParams({});
      setPaginationData({});
      setSearchParams({}, { replace: true });
      return;
    }

    // Add date range if provided
    const currentStartDate =
      additionalParams.start_date !== undefined ? additionalParams.start_date : startDate;
    const currentEndDate =
      additionalParams.end_date !== undefined ? additionalParams.end_date : endDate;
    const currentPreset =
      additionalParams.date_preset !== undefined ? additionalParams.date_preset : datePreset;

    if (currentStartDate) data.start_date = currentStartDate;
    if (currentEndDate) data.end_date = currentEndDate;

    setActiveSearchParams(data);
    setIsLoading(true);

    const params = {
      page_number: additionalParams.page_number || pageNumber,
      ...data,
    };

    // Update URL params
    const urlParams = {
      lot: searchTerm1,
      sku: searchTerm2,
      po: searchTerm3,
      page: params.page_number,
      start_date: currentStartDate,
      end_date: currentEndDate,
    };

    // Only include date_preset if it has a value
    if (currentPreset) {
      urlParams.date_preset = currentPreset;
    }

    updateUrlParams(urlParams);

    vendorReport({ data: params })
      .then((response) => {
        setLoading(false);
        if (response?.data?.status) {
          setReportData([]);
          popup(POPUP_TYPE.ERR, response.data.status);
        } else {
          setReportData(response.data?.records || []);
          setPaginationData(response.data?.params || {});
        }
      })
      .catch(() => {
        setLoading(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Initial load - read from URL params
  useEffect(() => {
    const urlLot = searchParams.get('lot');
    const urlSku = searchParams.get('sku');
    const urlPo = searchParams.get('po');
    const urlPage = searchParams.get('page');
    const urlStartDate = searchParams.get('start_date');
    const urlEndDate = searchParams.get('end_date');
    const urlDatePreset = searchParams.get('date_preset');

    let hasChanges = false;

    if (urlLot && urlLot !== searchTerm1) {
      setSearchTerm1(urlLot);
      hasChanges = true;
    }

    if (urlSku && urlSku !== searchTerm2) {
      setSearchTerm2(urlSku);
      hasChanges = true;
    }

    if (urlPo && urlPo !== searchTerm3) {
      setSearchTerm3(urlPo);
      hasChanges = true;
    }

    if (urlPage && parseInt(urlPage) !== pageNumber) {
      setPageNumber(parseInt(urlPage));
      hasChanges = true;
    }

    if (urlStartDate && urlStartDate !== startDate) {
      setStartDate(urlStartDate);
      hasChanges = true;
    }

    if (urlEndDate && urlEndDate !== endDate) {
      setEndDate(urlEndDate);
      hasChanges = true;
    }

    if (urlDatePreset && urlDatePreset !== datePreset) {
      setDatePreset(urlDatePreset);
      hasChanges = true;
    }

    // Fetch data if we have search params in URL
    if ((urlLot || urlSku || urlPo) && !hasChanges) {
      getReport();
    }
  }, []);

  // Handle page number changes
  useEffect(() => {
    const currentUrlPage = searchParams.get('page');
    const hasSearchTerms = searchTerm1 || searchTerm2 || searchTerm3;

    if (currentUrlPage !== pageNumber.toString() && pageNumber !== 0 && hasSearchTerms) {
      getReport();
    }
  }, [pageNumber]);

  useEffect(() => {
    if (search) {
      setPageNumber(1);
      getReport({ page_number: 1 });
      setSearch(false);
    }
  }, [search]);

  const handleClearSearch = (searchTermNumber) => {
    const updatedSearchTerms = {
      1: searchTermNumber === 1 ? '' : searchTerm1,
      2: searchTermNumber === 2 ? '' : searchTerm2,
      3: searchTermNumber === 3 ? '' : searchTerm3,
    };

    // Update the specific search term
    switch (searchTermNumber) {
      case 1:
        setSearchTerm1('');
        break;
      case 2:
        setSearchTerm2('');
        break;
      case 3:
        setSearchTerm3('');
        break;
      default:
        break;
    }

    // Check if any search terms remain after clearing
    const hasRemainingTerms =
      updatedSearchTerms[1] || updatedSearchTerms[2] || updatedSearchTerms[3];

    if (hasRemainingTerms) {
      setTimeout(() => {
        setPageNumber(1);
        setSearch(true);
      }, 0);
    } else {
      setReportData([]);
      setActiveSearchParams({});
      setPaginationData({});
      setPageNumber(1);
      setSearchParams({}, { replace: true });
    }
  };

  const handleSearch = () => {
    const trimmedLot = searchTerm1.trim();
    const trimmedSku = searchTerm2.trim();
    const trimmedPo = searchTerm3.trim();
    setSearchTerm1(trimmedLot);
    setSearchTerm2(trimmedSku);
    setSearchTerm3(trimmedPo);
    if (trimmedLot || trimmedSku || trimmedPo) {
      setSearch(true);
    }
  };

  const handleDateApply = ({ start, end, preset }) => {
    setStartDate(start);
    setEndDate(end);
    setDatePreset(preset || '');
    setPageNumber(1);

    getReport({
      page_number: 1,
      start_date: start,
      end_date: end,
      date_preset: preset || '',
    });
  };

  const handlePresetChange = (preset) => {
    setDatePreset(preset || '');
  };

  const handleClearFilter = () => {
    setReportData([]);
    setActiveSearchParams({});
    setSearchTerm1('');
    setSearchTerm2('');
    setSearchTerm3('');
    setPaginationData({});
    setPageNumber(1);
    setStartDate('');
    setEndDate('');
    setDatePreset('');
    setShowDateFilter(false);
    setSearchParams({}, { replace: true });
  };

  const handlePrintReport = () => {
    const data = activeSearchParams;

    setIsPrintLoading(true);
    exportWipReport({ data })
      .then((response) => {
        setIsPrintLoading(false);
        if (response?.url) {
          window.open(response.url, '_blank');
          popup(POPUP_TYPE.SUCCESS, 'WIP report exported successfully');
        } else {
          popup(POPUP_TYPE.ERR, 'Failed to export WIP report');
        }
      })
      .catch((error) => {
        setIsPrintLoading(false);
        dispatch(setLoaderVisibility(false));
        popup(POPUP_TYPE.ERR, error.message || 'Failed to export WIP report');
      });
  };

  return (
    <>
      <TitleBar title={[{ text: TITLES.REPORTS, route: ROUTES.REPORTS }]} />
      <div className="d-flex justify-content-start gap-2 mb-2">
        <SearchBarReport
          setSearchTerm={setSearchTerm1}
          searchTerm={searchTerm1}
          placeholder={'Lot Name'}
          onClear={() => handleClearSearch(1)}
        />
        <SearchBarReport
          setSearchTerm={setSearchTerm2}
          searchTerm={searchTerm2}
          placeholder={'SKU Name'}
          onClear={() => handleClearSearch(2)}
        />
        <SearchBarReport
          setSearchTerm={setSearchTerm3}
          searchTerm={searchTerm3}
          placeholder={'PO / V. Ref'}
          onClear={() => handleClearSearch(3)}
        />
        <Button
          clickHandler={handleSearch}
          className={`search-btn ${
            (!searchTerm1 && !searchTerm2 && !searchTerm3) ||
            (searchTerm1 === (searchParams.get('lot') || '') &&
              searchTerm2 === (searchParams.get('sku') || '') &&
              searchTerm3 === (searchParams.get('po') || ''))
              ? 'disabled'
              : ''
          } d-flex align-items-center justify-content-center px-3`}
          disabled={
            (!searchTerm1 && !searchTerm2 && !searchTerm3) ||
            (searchTerm1 === (searchParams.get('lot') || '') &&
              searchTerm2 === (searchParams.get('sku') || '') &&
              searchTerm3 === (searchParams.get('po') || ''))
          }>
          {BUTTON.SEARCH}
        </Button>
        <Button
          clickHandler={handleClearFilter}
          className={`search-btn ${
            reportData?.length === 0 && !startDate && !endDate ? 'disabled' : ''
          } d-flex align-items-center justify-content-center px-3`}
          disabled={reportData?.length === 0 && !startDate && !endDate}>
          {BUTTON.CLEAR_FILTERS}
        </Button>
        <Button
          clickHandler={handlePrintReport}
          className={`search-btn ${
            reportData?.length === 0 || isPrintLoading ? 'disabled' : ''
          } d-flex align-items-center justify-content-center px-3`}
          disabled={reportData?.length === 0 || isPrintLoading}>
          {isPrintLoading && (
            <span className="spinner-border spinner-border-sm text-white me-2" role="status" />
          )}
          {isPrintLoading ? 'Printing...' : BUTTON.PRINT}
        </Button>
      </div>

      <div className="d-flex align-items-center gap-3 mb-3">
        <Button className="blue-btn px-3" clickHandler={() => setShowDateFilter(!showDateFilter)}>
          {showDateFilter ? 'Hide Date Range' : 'Apply Date Range'}
        </Button>
      </div>

      <div className={`collapse ${showDateFilter ? 'show' : ''} mb-3`}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          preset={datePreset}
          onApply={handleDateApply}
          onPresetChange={handlePresetChange}
        />
      </div>

      {reportData.length ? (
        <PaginatedTable
          columns={REPORTS_COLUMN}
          initialData={reportData}
          colFilter={true}
          compiler={{ use: true, tool: reportDataCompiler }}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          paginationData={paginationData}
          isLoading={isLoading}
        />
      ) : (
        <div>
          <h4 className="text-center px-1 py-5">No data available, Please search..!!</h4>
        </div>
      )}
    </>
  );
};
